import type { MetaFunction } from "@remix-run/node";
import { json, LoaderFunctionArgs } from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";
import { getJobs } from "~/utils/matching.server";
import { getUser, getUserId } from "~/utils/session.server";

export const meta: MetaFunction = () => {
  return [
    { title: "FerryPros - Connecting Aircraft Owners and Brokers with Trusted Service Providers and Pilots" },
    { name: "description", content: "FerryPros is a marketplace connecting aircraft owners, brokers, and ferry pilot agencies with trusted pilots through secure escrow and real-time tracking." },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  // Check if user is logged in
  const userId = await getUserId(request);
  const user = userId ? await getUser(request) : null;

  // Fetch featured and recent jobs for the homepage
  const featuredJobs = await getJobs({ 
    status: 'Open',
    featured_first: true,
    // Include location-based information if user is logged in
    ...(user ? {
      userLocation: user.current_location,
      preferredLocation: user.preferred_location,
      sortByDistance: false // Don't sort by distance on homepage, keep featured first
    } : {})
  });

  // Limit to 4 jobs for the homepage display
  return json({ 
    jobs: featuredJobs.slice(0, 4),
    user
  });
}

export default function Index() {
  const { jobs, user } = useLoaderData<typeof loader>();

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gray-800 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-40">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-gray-800"></div>
          <div className="absolute inset-0" style={{ 
            backgroundImage: "url('/images/home-banner.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'left center',
            mixBlendMode: 'overlay'
          }}></div>
        </div>
        <div className="container-custom relative z-10 py-20 md:py-32">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6 leading-tight">
              Welcome to FerryPros
            </h1>
            <p className="text-xl md:text-2xl text-primary-100 mb-10 leading-relaxed">
              The premier marketplace connecting aircraft owners and brokers with trusted service providers and pilots through secure escrow and real-time tracking.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="/notification-signup" className="btn-primary bg-primary-600 text-white hover:bg-primary-700 text-lg px-8 py-3">
                Get Notified
              </a>
              <a href="/about" className="btn-secondary bg-white text-primary-600 border border-primary-600 hover:bg-gray-100 hover:text-primary-700 text-lg px-8 py-3">
                Learn More
              </a>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-900 to-transparent"></div>
      </section>

      {/* Marketing Video Section */}
      {/*<section className="py-16 bg-gray-100">*/}
      {/*  <div className="container-custom">*/}
      {/*    <div className="text-center mb-10">*/}
      {/*      <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">See FerryPros in Action</h2>*/}
      {/*      <div className="w-24 h-1 bg-primary-300 mx-auto mb-6"></div>*/}
      {/*      <p className="text-xl text-gray-700 max-w-3xl mx-auto">*/}
      {/*        Watch how our platform connects aircraft owners, brokers, and ferry pilot agencies with trusted pilots.*/}
      {/*      </p>*/}
      {/*    </div>*/}

      {/*    <div className="max-w-4xl mx-auto rounded-lg overflow-hidden shadow-xl">*/}
      {/*      <div className="relative" style={{ paddingBottom: '56.25%' }}> /!* 16:9 aspect ratio *!/*/}
      {/*        <video */}
      {/*          className="absolute top-0 left-0 w-full h-full"*/}
      {/*          controls*/}
      {/*          poster="/images/ferrypros-demo-thumbnail.jpg"*/}
      {/*          preload="metadata"*/}
      {/*        >*/}
      {/*          <source src="/videos/ferrypros-demo.mp4" type="video/mp4" />*/}
      {/*          Your browser does not support the video tag.*/}
      {/*        </video>*/}
      {/*      </div>*/}
      {/*      <div className="bg-white p-6">*/}
      {/*        <h3 className="text-xl font-semibold mb-2">FerryPros Platform Demo</h3>*/}
      {/*        <p className="text-gray-600">*/}
      {/*          This demonstration shows how users can post jobs, find qualified pilots, track flights in real-time, and complete secure transactions through our escrow system.*/}
      {/*        </p>*/}
      {/*      </div>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</section>*/}

      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">How It Works</h2>
            <div className="w-24 h-1 bg-primary-300 mx-auto mb-6"></div>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Our platform simplifies the aircraft ferry process with a secure, transparent system.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            <div className="card hover:shadow-lg transition-shadow duration-300">
              <div className="rounded-full bg-primary-600 w-16 h-16 flex items-center justify-center mb-6 text-white">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </div>
              <h3 className="text-xl font-display font-semibold mb-3">For Aircraft Owners</h3>
              <p className="text-gray-700">Post your ferry job, fund the secure escrow, and select from qualified pilots. Track your aircraft in real-time throughout the journey.</p>
            </div>

            <div className="card hover:shadow-lg transition-shadow duration-300">
              <div className="rounded-full bg-primary-600 w-16 h-16 flex items-center justify-center mb-6 text-white">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-display font-semibold mb-3">For Pilots</h3>
              <p className="text-gray-700">Browse available jobs, apply with your credentials, and get paid securely upon completion. Build your reputation with every successful ferry.</p>
            </div>

            <div className="card hover:shadow-lg transition-shadow duration-300">
              <div className="rounded-full bg-primary-600 w-16 h-16 flex items-center justify-center mb-6 text-white">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-display font-semibold mb-3">For Aircraft Brokers</h3>
              <p className="text-gray-700">Manage multiple aircraft and ferry jobs for your clients. Streamline your operations with our comprehensive management tools and secure payment system.</p>
            </div>

            <div className="card hover:shadow-lg transition-shadow duration-300">
              <div className="rounded-full bg-primary-600 w-16 h-16 flex items-center justify-center mb-6 text-white">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-display font-semibold mb-3">For Ferry Pilot Agencies</h3>
              <p className="text-gray-700">Manage your team of pilots and coordinate multiple ferry jobs. Access specialized tools to optimize your operations and grow your ferry pilot agency.</p>
            </div>

            <div className="card hover:shadow-lg transition-shadow duration-300 md:col-span-2 mt-4">
              <div className="rounded-full bg-primary-600 w-16 h-16 flex items-center justify-center mb-6 text-white">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-display font-semibold mb-3">Secure Transactions</h3>
              <p className="text-gray-700">Our escrow system ensures funds are only released when jobs are completed successfully, protecting all parties involved in the ferry process.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-900">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">Why Choose FerryPros?</h2>
            <div className="w-24 h-1 bg-primary-300 mx-auto mb-6"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We've built a platform that prioritizes safety, security, and transparency.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-14 h-14 bg-gray-800 rounded-full flex items-center justify-center text-primary-200">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-display font-semibold mb-3">Verified Pilots</h3>
                <p className="text-gray-300">All pilots are thoroughly vetted with background checks and credential verification, ensuring you work with only the most qualified professionals.</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-14 h-14 bg-gray-800 rounded-full flex items-center justify-center text-primary-200">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-display font-semibold mb-3">Secure Payments</h3>
                <p className="text-gray-300">Our escrow system ensures fair transactions for both parties, with funds only released when jobs are completed to satisfaction.</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-14 h-14 bg-gray-800 rounded-full flex items-center justify-center text-primary-200">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-display font-semibold mb-3">Real-time Tracking</h3>
                <p className="text-gray-300">Monitor your aircraft's journey with integrated ADS-B tracking, providing peace of mind throughout the entire ferry process.</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-14 h-14 bg-gray-800 rounded-full flex items-center justify-center text-primary-200">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-display font-semibold mb-3">Transparent Ratings</h3>
                <p className="text-gray-300">Make informed decisions based on verified reviews and ratings from other aircraft owners and pilots in our community.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Jobs Section */}
      <section className="py-20 bg-white border-t border-gray-200">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">Featured Ferry Jobs</h2>
            <div className="w-24 h-1 bg-primary-300 mx-auto mb-6"></div>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Browse our latest ferry opportunities or post your own job to find qualified pilots.
            </p>
          </div>

          {jobs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No jobs available at the moment. Check back soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {jobs.map((job) => (
                <div key={job.id} className={`border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow ${job.is_featured ? 'border-yellow-400 border-2' : 'border-gray-200'}`}>
                  <div className="flex justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-semibold text-primary-600">{job.title}</h3>
                      {job.is_featured && (
                        <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                          Featured
                        </span>
                      )}
                    </div>
                    <span className="text-green-600 font-medium">${job.budget}</span>
                  </div>

                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
                    <div>
                      <span className="text-gray-600">Aircraft:</span> <span className="text-gray-800">{job.aircraft_type}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Date:</span> <span className="text-gray-800">{job.estimated_date}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">From:</span> <span className="text-gray-800">{job.origin}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">To:</span> <span className="text-gray-800">{job.destination}</span>
                    </div>
                    {user && job.distance !== undefined && (
                      <div className="col-span-2">
                        <span className="text-gray-600">Distance:</span> <span className="text-blue-600 font-medium">{parseInt(job.distance.toString())} miles</span> from your location
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Link 
                      to={`/jobs/${job.id}`} 
                      className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-10 text-center">
            <Link 
              to="/jobs" 
              className="inline-block px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              View All Jobs
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-gray-800 to-gray-900 text-white text-center">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 text-primary-100">Ready to Get Started?</h2>
            <p className="text-xl md:text-2xl text-primary-100 max-w-3xl mx-auto mb-10">
              Join the FerryPros community today as an aircraft owner, broker, pilot, or Ferry Pilot Agency and experience a new standard in aircraft ferry services.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="/notification-signup" className="btn-primary bg-primary-600 text-white hover:bg-primary-700 hover:text-white text-lg px-8 py-3">
                Get Notified
              </a>
              <a href="/contact" className="btn-secondary bg-white text-primary-600 border border-primary-600 hover:bg-gray-100 hover:text-primary-700 text-lg px-8 py-3">
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
